import { type FunctionComponent, useCallback } from "react";
import { graphql, useFragment } from "react-relay";

import { notEmpty } from "shared/lib/utils";

import { EntityLink } from "scmp-app/components/entity-link";
import { useSearchContext } from "scmp-app/components/search/contexts";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { SearchEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import { checkIfNewsSectionInFirstIndex } from "scmp-app/lib/section";
import type { itemSectionSearchResultListItemSection$key } from "scmp-app/queries/__generated__/itemSectionSearchResultListItemSection.graphql";

import { Container, SectionTitle, TitleContainer } from "./styles";

export type Props = {
  className?: string;
  reference: itemSectionSearchResultListItemSection$key;
};

export const SectionSearchResultListItem: FunctionComponent<Props> = ({ className, reference }) => {
  const section = useFragment(
    graphql`
      fragment itemSectionSearchResultListItemSection on Section {
        entityId
        fullSectionPath {
          name
          ...helpersCheckIfNewsSectionInFirstIndexSection
        }
        ...entityLink
      }
    `,
    reference,
  );

  const { currentValue, initialSearchId } = useSearchContext();

  const handleSectionClick = useCallback(() => {
    // GA4 21.2.1
    sendGA4Tracking({
      action: "click",
      category: "search",
      customized_parameters: {
        search_id: initialSearchId,
        search_text: currentValue,
        section_id: section.entityId,
      },
      subcategory: "result",
    } as SearchEvent);
  }, [currentValue, initialSearchId, section.entityId]);

  const sanitizedSectionName =
    section?.fullSectionPath
      ?.filter(
        (section, index) => notEmpty(section) && !checkIfNewsSectionInFirstIndex(section, index),
      )
      ?.map(section => section?.name) ?? [];

  return (
    <Container className={className}>
      <EntityLink onClick={handleSectionClick} reference={section}>
        <TitleContainer>
          <SectionTitle>{sanitizedSectionName.join(" / ")}</SectionTitle>
        </TitleContainer>
      </EntityLink>
    </Container>
  );
};

SectionSearchResultListItem.displayName = "SectionSearchResultListItem";
